import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FolderTabs from '../../components/common/FolderTabs';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import TeamMemberSpecialOpeningsPanel from '../../components/team-members/TeamMemberSpecialOpeningsPanel';
import TeamMemberTimeOffDialog from '../../components/team-members/TeamMemberTimeOffDialog';
import TeamMemberWorkingHoursPanel from '../../components/team-members/TeamMemberWorkingHoursPanel';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import StoreService from '../../services/storeService';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';

export const AGENDA_TABS = {
  WORKING_HOURS: 'working-hours',
  SPECIAL_OPENINGS: 'special-openings',
  TIME_OFF: 'time-off',
};

export const resolveAgendaTab = (tabParam) => (
  Object.values(AGENDA_TABS).includes(tabParam) ? tabParam : AGENDA_TABS.WORKING_HOURS
);

const tabPanelContentSx = { p: { xs: 2, sm: 3, md: 4 } };

const TeamMemberAgendaPage = () => {
  const { t } = useTranslation();
  const { storeId, teamMemberId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, loading: permissionsLoading } = usePermissions(storeId);
  const canUpdate = hasPermission(Permissions.TeamMembersUpdate);
  const activeTab = resolveAgendaTab(searchParams.get('tab'));

  const [teamMember, setTeamMember] = useState(null);
  const [storeOpeningHours, setStoreOpeningHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrorKey, setLoadErrorKey] = useState(null);

  const loadAgenda = useCallback(async () => {
    if (!storeId || !teamMemberId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadErrorKey(null);
      const [member, store] = await requestDeduplicator.deduplicate(
        `team-member-agenda-${storeId}-${teamMemberId}`,
        () => Promise.all([
          TeamMemberService.getTeamMember(teamMemberId, storeId),
          StoreService.getStore(storeId),
        ]),
      );
      setTeamMember(member);
      setStoreOpeningHours(store?.openingHours || []);
    } catch (error) {
      console.error('Failed to load team member agenda:', error);
      setLoadErrorKey('teamMembers.agenda.error.loading');
    } finally {
      setLoading(false);
    }
  }, [storeId, teamMemberId]);

  useEffect(() => {
    if (!permissionsLoading) {
      loadAgenda();
    }
  }, [loadAgenda, permissionsLoading]);

  const handleWorkingHoursSaved = useCallback((workingHours) => {
    setTeamMember((current) => (
      current ? { ...current, store: { ...current.store, workingHours } } : current
    ));
  }, []);

  const tabs = [
    { value: AGENDA_TABS.WORKING_HOURS, label: t('teamMembers.agenda.tabs.workingHours') },
    { value: AGENDA_TABS.SPECIAL_OPENINGS, label: t('teamMembers.agenda.tabs.specialOpenings') },
    { value: AGENDA_TABS.TIME_OFF, label: t('teamMembers.agenda.tabs.timeOff') },
  ];

  if (loading || permissionsLoading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('teamMembers.agenda.title')}
        pageSubtitle={t('teamMembers.agenda.subtitle')}
        variant="form"
      />
    );
  }

  return (
    <ManagementPageLayout
      id="team-member-agenda-page"
      pageTitle={teamMember?.name || t('teamMembers.agenda.title')}
      pageSubtitle={t('teamMembers.agenda.subtitle')}
      contentId="team-member-agenda-content"
    >
      <Button
        component={Link}
        to={`/management/stores/${storeId}/team-members`}
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        sx={{ mb: 2, borderColor: BRAND.accent, color: BRAND.accentText }}
      >
        {t('common.back')}
      </Button>

      {loadErrorKey ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={loadAgenda}>{t('common.retry')}</Button>}
        >
          {t(loadErrorKey)}
        </Alert>
      ) : (
        <FolderTabs
          tabs={tabs}
          value={activeTab}
          onChange={(nextTab) => setSearchParams(
            nextTab === AGENDA_TABS.WORKING_HOURS ? {} : { tab: nextTab },
            { replace: true },
          )}
          contentId="team-member-agenda-tabs-content"
          equalWidthOnMobile
        >
          <Box role="tabpanel" hidden={activeTab !== AGENDA_TABS.WORKING_HOURS} sx={{ minWidth: 0 }}>
            {activeTab === AGENDA_TABS.WORKING_HOURS ? (
              <Box sx={tabPanelContentSx}>
                <TeamMemberWorkingHoursPanel
                  storeId={storeId}
                  teamMemberId={teamMemberId}
                  workingHours={teamMember?.store?.workingHours}
                  storeOpeningHours={storeOpeningHours}
                  canUpdate={canUpdate}
                  onSaved={handleWorkingHoursSaved}
                />
              </Box>
            ) : null}
          </Box>
          <Box role="tabpanel" hidden={activeTab !== AGENDA_TABS.SPECIAL_OPENINGS} sx={{ minWidth: 0 }}>
            {activeTab === AGENDA_TABS.SPECIAL_OPENINGS ? (
              <Box sx={tabPanelContentSx}>
                <TeamMemberSpecialOpeningsPanel
                  storeId={storeId}
                  teamMemberId={teamMemberId}
                  canUpdate={canUpdate}
                />
              </Box>
            ) : null}
          </Box>
          <Box role="tabpanel" hidden={activeTab !== AGENDA_TABS.TIME_OFF} sx={{ minWidth: 0 }}>
            {activeTab === AGENDA_TABS.TIME_OFF ? (
              <Box sx={tabPanelContentSx}>
                <TeamMemberTimeOffDialog
                  open
                  embedded
                  teamMember={{ id: teamMemberId, name: teamMember?.name }}
                  storeId={storeId}
                />
              </Box>
            ) : null}
          </Box>
        </FolderTabs>
      )}
    </ManagementPageLayout>
  );
};

export default TeamMemberAgendaPage;
